import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { curriculumAPI, gamificationAPI, adaptiveAPI, participantAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import XpBar from '../components/gamification/XpBar';
import LeaderboardPanel from '../components/gamification/LeaderboardPanel';
import AccessDenied from '../components/AccessDenied';
import ChatWidget from '../components/ChatWidget';
import { useDockablePanel } from '../../../shared/components/DockablePanel';
import PreTestBanner from '../components/PreTestBanner';
import LSATDiagnosticModal from '../components/adaptive/LSATDiagnosticModal';
import LearnerNav from '../components/nav/LearnerNav';

const DIAGNOSTIC_DOMAINS = new Set(['lsat', 'sat']);

/** Convert a domain slug to a human-readable display title. */
function formatDomainTitle(slug, trackId) {
  const LABELS = {
    ai:               'AI',
    ai_orchestrator:  'AI Orchestrator',
    ai_beginner:      'AI Beginner',
    ai_entrepreneur:  'AI-Powered Entrepreneur',
    lsat:             'LSAT',
    sat:              'SAT',
    football:         'Football',
  };
  if (slug === 'ai' && trackId) {
    const TRACK_LABELS = {
      ai_fundamentals: 'AI Fundamentals',
      ai_orchestrator_certificate: 'AI Orchestrator Certificate',
      ai_entrepreneur_certificate: 'AI Entrepreneur Certificate',
    };
    return TRACK_LABELS[trackId] || LABELS[slug] || 'AI';
  }
  return LABELS[slug] || slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const TRACK_LABELS = {
  ai_fundamentals: 'AI Fundamentals',
  ai_orchestrator_certificate: 'AI Orchestrator Certificate',
  ai_entrepreneur_certificate: 'AI Entrepreneur Certificate',
};

function trackDisplayName(track) {
  const id = track?.track_id || '';
  const fromApi = (track?.display_name || '').trim();
  if (fromApi) return fromApi;
  if (TRACK_LABELS[id]) return TRACK_LABELS[id];
  if (id) return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return 'Track';}

const DIFF_META = {
  beginner:     { emoji: '🟢', label: 'Beginner',     color: '#22c55e' },
  intermediate: { emoji: '🟡', label: 'Intermediate', color: '#eab308' },
  advanced:     { emoji: '🔴', label: 'Advanced',     color: '#ef4444' },
};

/** API may return completion_pct as 0–1 (Mongo) or 0–100 (YAML engine). */
function displayCompletionPct(raw) {
  const n = Number(raw);
  if (raw == null || Number.isNaN(n)) return 0;
  if (n > 1) return Math.round(Math.min(100, n));
  return Math.round(n * 100);
}

/**
 * DomainPage — shows curriculum tracks and modules for an enrolled domain.
 *
 * Module three-state display:
 *   locked    — prerequisite not met; shown as locked, no action.
 *   unlocked  — ready to start or in-progress; shows "Start" / "Continue".
 *   mastered  — accuracy ≥ mastery gate AND minimum attempts met.
 *               Shows a ✓ badge PLUS a "Practice Again" button so the
 *               learner can replay a mastered module at any time.
 *
 * Replay flow: navigates to /session/{domain}/{scenarioId}?replay=true
 * The session API reads the `replay` query param and sets
 * StartSessionRequest.replay=True, which ParticipantService uses to
 * route XP to replay_bonus instead of first-time mastery bonus.
 */
export default function DomainPage() {
  const { domain } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // For the consolidated "ai" domain, a ?track= param selects which track to display.
  const trackParam = domain === 'ai' ? (searchParams.get('track') || null) : null;
  const { t } = useTranslation('curriculum');
  const tc = (key) => t(key, { ns: 'common' });
  const navigate = useNavigate();
  const { learner, activeCharacter } = useAuth();

  const [tracks, setTracks] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [gamProfile, setGamProfile] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [dismissedPretests, setDismissedPretests] = useState({});
  const [learnerDifficulty, setLearnerDifficulty] = useState(null);
  const [diffChecked, setDiffChecked] = useState(false);
  // Character archetype — used to pass character_class param to curriculum API
  // and to render supplemental scenes separately.
  const [learnerCharacterClass, setLearnerCharacterClass] = useState(null);
  // Supplemental modules state: { [trackId]: collapsed }
  const [supplementalCollapsed, setSupplementalCollapsed] = useState({});
  // tutorialId → { segmentIndex, hasProgress } from server + localStorage
  const [tutorialLeftOff, setTutorialLeftOff] = useState({});
  // LSAT/SAT diagnostic modal
  const [showDiagnosticModal, setShowDiagnosticModal] = useState(false);
  const [diagnosticChecked, setDiagnosticChecked] = useState(false);

  const {
    mode: chatMode, setMode: setChatMode,
    width: chatWidth, setWidth: setChatWidth,
    docked: chatDocked, bodyPadding: chatBodyPadding,
  } = useDockablePanel('teachiq_chat');

  const enrolledDomains = learner?.enrolled_domains || [];
  const isEnrolled = enrolledDomains.includes(domain);

  // Resolve character class: prefer activeCharacter from session; fall back to participant API.
  useEffect(() => {
    if (!isEnrolled) return;
    // Use activeCharacter if it's for this domain
    if (activeCharacter?.character_class && activeCharacter?.domain_id === domain) {
      setLearnerCharacterClass(activeCharacter.character_class);
      return;
    }
    // Otherwise fetch from participant API
    participantAPI.getParticipant()
      .then(({ data }) => {
        const chars = Array.isArray(data.characters) && data.characters.length > 0
          ? data.characters
          : Object.values(data.domain_characters || {}).filter((v) => v && typeof v === 'object');
        const domainChar = chars.find((c) => c.domain_id === domain);
        if (domainChar?.character_class) {
          setLearnerCharacterClass(domainChar.character_class);
        }
      })
      .catch(() => { /* non-fatal */ });
  }, [domain, isEnrolled, activeCharacter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEnrolled) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await curriculumAPI.getPreference(domain);
        if (cancelled) return;
        if (!data.difficulty) {
          const qs = trackParam
            ? `?track=${encodeURIComponent(trackParam)}`
            : '';
          navigate(`/dashboard/domain/${domain}/difficulty${qs}`, { replace: true });
          return;
        }
        setLearnerDifficulty(data.difficulty);
      } catch {
        // Non-fatal — don't block domain display if preference check fails
      }
      if (!cancelled) setDiffChecked(true);
    })();
    return () => { cancelled = true; };
  }, [domain, isEnrolled]); // eslint-disable-line react-hooks/exhaustive-deps

  // LSAT/SAT: check mastery on first visit — show diagnostic if new learner
  useEffect(() => {
    if (!isEnrolled || !diffChecked || !DIAGNOSTIC_DOMAINS.has(domain) || diagnosticChecked) return;
    if (!learner?.learner_id) return;
    setDiagnosticChecked(true);
    const storageKey = `diagnostic_declined_${domain}_${learner.learner_id}`;
    if (sessionStorage.getItem(storageKey)) return; // already decided this session

    adaptiveAPI.getMastery(learner.learner_id)
      .then(({ data }) => {
        const skills = data.skills || data || [];
        const isNewLearner =
          !Array.isArray(skills) ||
          skills.length === 0 ||
          skills.every((s) => !s.tier || s.tier === 'cold_start');
        if (isNewLearner) {
          setShowDiagnosticModal(true);
        }
      })
      .catch(() => {
        // Can't check mastery — assume new learner and show diagnostic option
        setShowDiagnosticModal(true);
      });
  }, [isEnrolled, diffChecked, domain, diagnosticChecked, learner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEnrolled || !diffChecked) return;
    (async () => {
      try {
        const trackParams = {
          ...(learnerCharacterClass ? { character_class: learnerCharacterClass } : {}),
          // For the unified AI domain, filter to the selected track when specified
          ...(domain === 'ai' && trackParam ? { track_id: trackParam } : {}),
        };
        const { data } = await curriculumAPI.listTracks(domain, trackParams);
        setTracks(data.tracks || []);

        const prog = {};
        for (const track of data.tracks || []) {
          try {
            const { data: p } = await curriculumAPI.getTrackProgress(domain, track.track_id, trackParams);
            prog[track.track_id] = p;
            if (p.difficulty && !learnerDifficulty) setLearnerDifficulty(p.difficulty);
          } catch { /* track may have no progress yet */ }
        }
        setProgress(prog);
      } catch { /* network error handled by empty state */ }

      try {
        const { data: gp } = await gamificationAPI.getProfile(domain);
        setGamProfile(gp);
      } catch { /* gamification optional */ }

      // Hydrate Start/Continue from server progress (not localStorage alone).
      try {
        const token = localStorage.getItem('scenario_os_token');
        const leftOff = {};
        const tutIds = new Set();
        for (const p of Object.values(prog || {})) {
          for (const mod of (p.modules || [])) {
            for (const tut of (mod.tutorials || [])) {
              if (tut?.tutorial_id) tutIds.add(tut.tutorial_id);
            }
            if (mod?.tutorial_id) tutIds.add(mod.tutorial_id);
          }
        }
        await Promise.all([...tutIds].map(async (tid) => {
          let serverIdx = null;
          let hasProgress = false;
          if (token) {
            try {
              const res = await fetch(
                `/scenario-os/curriculum/tutorial-progress/${encodeURIComponent(tid)}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              if (res.ok) {
                const data = await res.json();
                const idx = Number(data.segment_index);
                if (Number.isFinite(idx) && idx > 0) serverIdx = idx;
                hasProgress = Boolean(
                  (Number.isFinite(idx) && idx > 0)
                  || (data.answers && Object.keys(data.answers).length)
                  || (data.reflections && Object.keys(data.reflections).length)
                  || (data.segment_state && Object.keys(data.segment_state).length)
                  || data.completed,
                );
              }
            } catch { /* non-fatal */ }
          }
          const localIdx = readLeftOff(tid);
          const segmentIndex = serverIdx != null
            ? serverIdx
            : (localIdx != null && localIdx > 0 ? localIdx : null);
          if (segmentIndex != null || hasProgress || (localIdx != null && localIdx > 0)) {
            leftOff[tid] = {
              segmentIndex: segmentIndex != null ? segmentIndex : (localIdx || 0),
              hasProgress: true,
            };
          }
        }));
        setTutorialLeftOff(leftOff);
      } catch { /* non-fatal */ }

      setLoading(false);
    })();
  }, [domain, location.key, location.search, diffChecked, isEnrolled, learnerCharacterClass, trackParam]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isEnrolled) {
    return <AccessDenied domainId={domain} onBack={() => navigate('/')} />;
  }

  const TEST_PREP_DOMAINS = new Set(['lsat', 'sat']);

  const FULL_TEST_TRACKS = new Set([
    'lsat_full_test_practice',
    'lsat_preptest_full_test_practice',
    'sat_full_test_practice',
  ]);

  const resolveModuleTutorialId = (mod, preferredId = null) => {
    if (preferredId) return preferredId;
    const tutorials = mod?.tutorials || [];
    // Prefer a tutorial that already has left-off / progress.
    for (const tut of tutorials) {
      const tid = tut?.tutorial_id;
      if (tid && tutorialLeftOff[tid]?.hasProgress) return tid;
    }
    const fromList = tutorials[0]?.tutorial_id || mod?.tutorial_id || null;
    if (fromList) return fromList;
    // Last resort: canonical tut_<module_id> naming used by AI pack publish.
    // Covers API regressions where modules arrive without tutorials[] but the
    // published tutorial doc still exists (e.g. domain_id alias mismatch).
    const mid = mod?.module_id;
    return mid ? `tut_${mid}` : null;
  };

  const startModule = (
    trackId,
    moduleId,
    scenarioId,
    replay = false,
    tutorialId = null,
    { segmentIndex = null, forceStart = false } = {},
  ) => {
    const tid = tutorialId || null;
    // Full-test-practice modules: route to the dedicated test session player
    // which shows Timed / Untimed / Check-As-You-Go mode picker before the test.
    if (TEST_PREP_DOMAINS.has(domain) && FULL_TEST_TRACKS.has(trackId)) {
      if (scenarioId) {
        navigate(`/tests/${domain}/session/${scenarioId}`, {
          state: { track_id: trackId, module_id: moduleId },
        });
      } else if (tid) {
        navigate(`/tutorials/${domain}/play/${encodeURIComponent(tid)}`, {
          state: { track_id: trackId, module_id: moduleId, tutorial_id: tid, test_mode_picker: true },
        });
      } else {
        navigate(`/tests/${domain}`, {
          state: { category: moduleId, track_id: trackId },
        });
      }
      return;
    }
    // Prefer the tutorial player when a published tutorial exists — it renders
    // video / slides / quiz / lab / reflection / puzzle segments. Session player
    // is the fallback for modules that only have scenario_ids (no tutorial doc).
    if (tid) {
      let path = `/tutorials/${domain}/play/${encodeURIComponent(tid)}`;
      const qs = new URLSearchParams();
      // Keep AI track context so back-nav / coach surfaces stay on the same track.
      if (domain === 'ai' && trackParam) qs.set('track', trackParam);
      if (forceStart) {
        // Explicit "Start over" — clear bookmarks and begin at step 1 with gates on.
        qs.set('replay', 'true');
        try { localStorage.removeItem(progressKey(tid)); } catch { /* ignore */ }
      } else if (replay) {
        // Mastered module "Review" — restore saved answers, skip quiz gates,
        // open near the end of the lesson (player defaults to last segment).
        qs.set('review', 'true');
      } else {
        const leftOff = segmentIndex != null ? segmentIndex : readLeftOff(tid);
        if (leftOff != null && Number.isFinite(Number(leftOff)) && Number(leftOff) >= 0) {
          qs.set('segment', String(Math.floor(Number(leftOff))));
        }
      }
      const q = qs.toString();
      if (q) path = `${path}?${q}`;
      navigate(path, {
        state: {
          track_id: trackId,
          module_id: moduleId,
          tutorial_id: tid,
          review: !!replay && !forceStart,
        },
      });
      return;
    }
    if (TEST_PREP_DOMAINS.has(domain) && !tid && !scenarioId) {
      navigate(`/tests/${domain}`, {
        state: { category: moduleId, track_id: trackId },
      });
      return;
    }
    // Last resort: only hit session player when there is truly no tutorial id.
    const sessionKey = scenarioId || moduleId;
    const path = `/session/${domain}/${sessionKey}`;
    navigate(replay ? `${path}?replay=true` : path, {
      state: { track_id: trackId, module_id: moduleId, scenario_id: scenarioId || null },
    });
  };

  // Friendly labels for segment_type codes from the API.
  // The badge list itself is NOT hardcoded — it is derived from each
  // tutorial's segments (add/remove scenes → badges update after refresh).
  const SEGMENT_LABELS = {
    explanation: 'Video',
    video: 'Video',
    slide_deck: 'Slides',
    question: 'Quiz',
    quiz: 'Quiz',
    reflection: 'Reflect',
    ai_lab: 'AI Lab',
    scenario: 'Practice',
    demonstration: 'Demo',
    summary: 'Summary',
    hook: 'Intro',
    content_panel: 'Reading',
    reading: 'Reading',
    prompt_canvas: 'Canvas',
    curated_resource: 'Resource',
    code_lab: 'Code Lab',
    capstone: 'Capstone',
    project: 'Project',
    puzzle: 'Puzzle',
    flashcard: 'Flashcard',
    flashcard_scene: 'Flashcard',
    timed_test_session: 'Timed Test',
    untimed_practice: 'Practice Test',
  };

  // Map segment_type → display label. Prefer Video only when a real video_url exists.
  // Audio-only / flashcard segments must not inherit the Video badge (Hova 09-07/09-09).
  const labelForSegmentType = (type, _tut, seg = null) => {
    const ct = (seg?.content_type || '').toLowerCase();
    if (ct === 'flashcard' || type === 'flashcard' || type === 'flashcard_scene') {
      return 'Flashcard';
    }
    if (seg?.has_audio && !seg?.has_video) {
      return 'Audio';
    }
    if ((type === 'explanation' || type === 'content_panel') && seg?.has_video) {
      return 'Video';
    }
    if (type === 'explanation' && !seg?.has_video) {
      // Keep Video default for explanation chips on locked cards without
      // per-seg payloads; content_panel stays Reading.
      return SEGMENT_LABELS.explanation;
    }
    return SEGMENT_LABELS[type] || (typeof type === 'string'
      ? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      : type);
  };

  // Case-normalize + dedupe for module-card type pills (FUND-02).
  const uniqueTypeBadges = (types) => {
    const seen = new Set();
    const out = [];
    for (const type of types || []) {
      const label = labelForSegmentType(type);
      const key = String(label || '').toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(label);
    }
    return out;
  };

  const progressKey = (tutorialId) =>
    `teachiq:tutorial_progress:${learner?.learner_id || 'anon'}:${tutorialId}`;

  const readLeftOff = (tutorialId) => {
    try {
      const raw = localStorage.getItem(progressKey(tutorialId));
      if (!raw) return null;
      const data = JSON.parse(raw);
      const idx = parseInt(data?.segmentIndex, 10);
      return Number.isFinite(idx) && idx > 0 ? idx : null;
    } catch {
      return null;
    }
  };

  const toggleModuleLessons = (moduleId) =>
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));

  const toggleSupplemental = (trackId) =>
    setSupplementalCollapsed((prev) => ({ ...prev, [trackId]: !prev[trackId] }));

  if (!diffChecked) return <div className="page-loading">{tc('loading')}</div>;
  if (loading) return <div className="page-loading">{tc('loading')}</div>;

  const diffMeta = learnerDifficulty ? DIFF_META[learnerDifficulty] : null;

  const domainPageDockStyle = chatDocked ? {
    paddingRight: chatBodyPadding + 24,
    transition: 'padding-right 0.2s',
  } : {};

  return (
    <div className="domain-page" style={domainPageDockStyle}>
      <LearnerNav />

      {/* LSAT/SAT new-learner diagnostic prompt */}
      {showDiagnosticModal && (
        <LSATDiagnosticModal
          domain={domain}
          learnerId={learner?.learner_id || ''}
          onConfirm={(sessionData) => {
            setShowDiagnosticModal(false);
            if (sessionData?.session_id) {
              navigate(`/session/${domain}/${sessionData.session_id}?mode=diagnostic`);
            }
          }}
          onDecline={() => {
            const storageKey = `diagnostic_declined_${domain}_${learner?.learner_id}`;
            sessionStorage.setItem(storageKey, '1');
            setShowDiagnosticModal(false);
          }}
        />
      )}

      {/* Content column — centered, cream background inherited from .domain-page */}
      <div className="domain-page-inner">

      <header className="page-header" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            className="btn-text"
            onClick={() => navigate('/dashboard')}
            style={{ alignSelf: 'flex-start', fontSize: '0.9rem', padding: 0 }}
            aria-label="Back to dashboard"
          >
            ← Dashboard
          </button>
          <h1 style={{ margin: 0 }}>{formatDomainTitle(domain, trackParam)}</h1>
        </div>
        {/* AI track selector — shown when learner has 2+ enrolled AI tracks */}
        {domain === 'ai' && (() => {
          const aiTracks = (learner?.enrolled_tracks?.ai || [])
            .map((t) => (typeof t === 'string' ? t : t?.track_id))
            .filter(Boolean);
          if (aiTracks.length < 2) return null;
          const activeTrack = trackParam || aiTracks[0];
          return (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {aiTracks.map((tid) => {
                const label = TRACK_LABELS[tid] || tid.replace(/_/g, ' ');
                const isActive = activeTrack === tid;
                return (
                  <button
                    key={tid}
                    type="button"
                    className={isActive ? 'btn-small btn-start' : 'btn-small'}
                    style={isActive
                      ? { fontWeight: 700 }
                      : {
                          background: '#FFFFFF',
                          color: 'var(--teachiq-dark, #1E1E24)',
                          border: '1px solid #FFCF99',
                          fontWeight: 600,
                        }}
                    onClick={() => navigate(`/domain/ai?track=${encodeURIComponent(tid)}`)}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          );
        })()}
        {/* Difficulty badge — shows current level and a "Change" link */}
        {diffMeta && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 9999,
              background: `${diffMeta.color}20`,
              border: `1px solid ${diffMeta.color}50`,
              color: diffMeta.color,
              fontSize: '0.8rem', fontWeight: 600,
            }}>
              {diffMeta.emoji} My level: {diffMeta.label}
            </span>
            <button
              className="btn-text"
              style={{ fontSize: '0.8rem', textDecoration: 'underline' }}
              onClick={() => navigate(`/dashboard/domain/${domain}/difficulty`)}
            >
              Change
            </button>
          </div>
        )}
        {/* The standalone /tutorials/:domain index was removed because
            lessons are surfaced (and played) in two well-discovered
            places already — the curriculum modules below and the
            Content Library on the coach side — and the dedicated index
            page kept landing users in dead-ends when filters returned
            zero rows. Lessons launch directly from a module's "Play"
            action via the curriculum tracks rendered below. */}
      </header>

      {gamProfile && (
        <div className="domain-gamification">
          <XpBar
            xpCurrent={(gamProfile.xp_total || 0) % (gamProfile.xp_to_next_level || 500)}
            xpToNext={gamProfile.xp_to_next_level || 500}
            level={gamProfile.level || 1}
            levelName={gamProfile.level_title || ''}
            xpEarned={0}
          />
          <button className="btn-small" onClick={() => setShowLeaderboard(!showLeaderboard)}>
            {showLeaderboard ? 'Hide Leaderboard' : 'Leaderboard'}
          </button>
        </div>
      )}

      {showLeaderboard && <LeaderboardPanel domain={domain} />}

      <h2>{t('your_tracks')}</h2>

      {/* Practice Tests entry point — only for test-prep domains (lsat, sat) that have
          a full_test_practice track. Checking the loaded tracks avoids hardcoding names. */}
      {tracks.some((tr) => tr.track_id?.includes('full_test_practice')) && (
      <div style={{ marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button
          className="btn-secondary"
          onClick={() => {
            const el = document.getElementById('track-lsat_full_test_practice') || document.getElementById('track-sat_full_test_practice');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              el.style.outline = '2px solid #1e3a5f';
              el.style.borderRadius = '12px';
              setTimeout(() => { el.style.outline = 'none'; }, 2000);
            }
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8,
            border: '2px solid #1e3a5f', background: '#fff',
            color: '#1e3a5f', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
          }}
        >
          Practice Tests
        </button>

        {/* Show certificate link when at least one module is mastered */}
        {Object.values(progress).some((p) => p?.modules?.some((m) => m.is_mastered)) && (
          <button
            className="btn-secondary"
            onClick={() => navigate(`/dashboard/certificate/${domain}`)}
          >
            🎓 View Certificate
          </button>
        )}
      </div>
      )}

      <ChatWidget
        domain={domain}
        panelMode={chatMode}
        onPanelModeChange={setChatMode}
        panelWidth={chatWidth}
        onPanelResize={setChatWidth}
      />

      <div className="tracks-list">
        {tracks.map((track) => {
          const prog = progress[track.track_id];
          return (
            <div key={track.track_id} id={`track-${track.track_id}`} className="track-card">
              <h3>{trackDisplayName(track)}</h3>
              <p>{track.description}</p>
              <div className="track-meta">
                <span>
                  {t('modules')}:{' '}
                  {prog ? (() => {
                    // Prefer server-provided counts; fall back to counting from modules array.
                    const total = track.module_count ?? (prog.modules || []).length;
                    const completed = prog.modules_completed
                      ?? (prog.modules || []).filter(
                          (m) => m.is_mastered && ((m.attempts_count ?? m.attempts ?? 0) > 0)
                        ).length;
                    // Fix: if server reports 0% but we have mastered modules, derive from counts.
                    // This covers the case where completion_pct is not updated when mastery has no quiz score.
                    const rawPct = displayCompletionPct(prog.completion_pct);
                    const pct = (rawPct === 0 && total > 0 && completed > 0)
                      ? Math.round((completed / total) * 100)
                      : rawPct;
                    return `${pct}% Complete (${completed}/${total})`;
                  })() : track.module_count}
                </span>
              </div>

              {(() => {
                const allMods = prog?.modules || [];
                // Separate primary modules from supplemental ones.
                // is_supplemental is set by the backend when character_class filtering
                // is active and a module falls outside the learner's archetype audience.
                const primaryMods = allMods.filter((m) => !m.is_supplemental);
                const supplementalMods = allMods.filter((m) => m.is_supplemental);
                const isSupplementalCollapsed = supplementalCollapsed[track.track_id] !== false; // default collapsed

                const renderModule = (mod) => {
                // Note: mod.difficulty is a content label (e.g. "beginner") for display
                // purposes only — it does NOT restrict which modules are visible. All
                // modules are shown to all learners; the learner's difficulty preference
                // determines which variant of curated/canvas segments is surfaced inside
                // the lesson, not which modules appear in the curriculum view.
                const scenarioId = mod.scenario_ids?.[0] || null;
                const tutorials = mod.tutorials || [];
                const hasLessons = tutorials.length > 0;
                const hasScenarios = (mod.scenario_ids || []).length > 0;
                const canStart = mod.is_unlocked;
                // Guard: only show mastered state when there's actual evidence of completion
                // (attempts > 0 or accuracy defined). Prevents false ✓ on 0%-complete modules.
                const hasAttempts = (mod.attempts_count ?? mod.attempts ?? 0) > 0;
                const isActuallyMastered = mod.is_mastered && (hasAttempts || mod.accuracy != null || mod.best_accuracy != null);
                const stateClass = isActuallyMastered ? 'mastered' : canStart ? 'unlocked' : 'locked';
                const primaryTutorialId = resolveModuleTutorialId(mod);
                const isMultiLesson = tutorials.length > 1;
                const isExpanded = !!expandedModules[mod.module_id];
                const leftOffMeta = primaryTutorialId ? tutorialLeftOff[primaryTutorialId] : null;
                const leftOffIdx = leftOffMeta?.segmentIndex != null
                  ? leftOffMeta.segmentIndex
                  : (primaryTutorialId ? readLeftOff(primaryTutorialId) : null);
                const canResume = !isActuallyMastered && (
                  (leftOffIdx != null && leftOffIdx >= 0 && !!leftOffMeta?.hasProgress)
                  || !!leftOffMeta?.hasProgress
                  || (mod.attempts_count ?? mod.attempts ?? 0) > 0
                );

                // Button label
                const startLabel = isActuallyMastered
                  ? t('review_module', { defaultValue: 'Review' })
                  : canResume
                    ? t('continue_module', { defaultValue: 'Continue' })
                    : hasLessons ? 'Start Lesson' : t('start_module');

                // Aggregate type badges across all tutorials in the module
                const typeBadges = uniqueTypeBadges(
                  tutorials.flatMap((tut) => tut.segment_types || []),
                );

                return (
                  <div key={mod.module_id} className={`module-row ${stateClass}`}>
                    {/* State icon — status only (not an expand control) */}
                    <span
                      className="module-icon"
                      title={isActuallyMastered ? 'Completed' : canStart ? 'Unlocked' : 'Locked'}
                    >
                      {isActuallyMastered ? '✓' : canStart ? '●' : '🔒'}
                    </span>

                    {/* Name + lesson metadata */}
                    <div className="module-info">
                      <span className="module-name">{mod.display_name}</span>

                      {/* Pre-test pass badge */}
                      {mod.completion_type === 'pretest_pass' && (
                        <span
                          className="module-pretest-badge"
                          title="Completed via pre-test"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '2px 8px', borderRadius: 9999, marginLeft: 6,
                            background: 'rgba(79,70,229,0.12)',
                            border: '1px solid rgba(79,70,229,0.4)',
                            color: '#4f46e5',
                            fontSize: '0.72rem', fontWeight: 600,
                          }}
                        >
                          🚀 Skipped via pre-test ✓
                        </span>
                      )}

                      {/* Type badges — always show when we have segment_types */}
                      {typeBadges?.length > 0 && (
                        <div className="lesson-segments">
                          {typeBadges.map((label) => (
                            <span key={label} className="segment-badge">
                              {label}
                            </span>
                          ))}
                          {!isMultiLesson && tutorials[0]?.segment_count > 0 && (
                            <span className="segment-count">{tutorials[0].segment_count} steps</span>
                          )}
                          {canResume && (
                            <span className="segment-count" title="Saved progress in this browser">
                              Left off at step {leftOffIdx + 1}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Expand to see lessons / steps (works for 1+ tutorials) */}
                      {hasLessons && (
                        <button
                          type="button"
                          className="btn-text btn-lessons-toggle"
                          onClick={() => toggleModuleLessons(mod.module_id)}
                          aria-expanded={isExpanded}
                        >
                          {isMultiLesson
                            ? `${tutorials.length} lessons `
                            : `${tutorials[0]?.segment_count || 0} steps `}
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      )}
                    </div>

                    {mod.difficulty && (() => {
                      const diffMap = {
                        beginner:     { emoji: '🟢', label: 'Beginner',     color: '#22c55e' },
                        intermediate: { emoji: '🟡', label: 'Intermediate', color: '#eab308' },
                        advanced:     { emoji: '🔴', label: 'Advanced',     color: '#ef4444' },
                      };
                      const diff = diffMap[mod.difficulty] || { emoji: '⚪', label: mod.difficulty, color: '#94a3b8' };
                      const isMismatch = learnerDifficulty && learnerDifficulty !== mod.difficulty;
                      return (
                        <span
                          className="module-difficulty"
                          title={isMismatch ? `Content level (your level: ${learnerDifficulty})` : 'Content level'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '2px 8px', borderRadius: 9999,
                            background: `${diff.color}20`,
                            border: `1px solid ${diff.color}50`,
                            color: diff.color,
                            fontSize: '0.75rem', fontWeight: 600,
                            opacity: isMismatch ? 0.7 : 1,
                          }}>
                          {diff.emoji} {diff.label}
                        </span>
                      );
                    })()}

                    {isActuallyMastered && (mod.accuracy !== undefined || mod.best_accuracy !== undefined) && (
                      <span className="module-accuracy" title={t('accuracy_tooltip')}>
                        {Math.round((mod.accuracy ?? mod.best_accuracy ?? 0) * 100)}%
                      </span>
                    )}

                    <div className="module-actions">
                      {canStart && (
                        <>
                          <button
                            type="button"
                            className={`btn-small ${isActuallyMastered ? 'btn-replay' : 'btn-start'}`}
                            disabled={!primaryTutorialId && !scenarioId}
                            title={!primaryTutorialId && !scenarioId ? 'No lesson linked to this module yet' : undefined}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const tid = resolveModuleTutorialId(mod, primaryTutorialId);
                              if (!tid && !scenarioId) return;
                              // Mastered → Review mode. In-progress → resume segment.
                              // Fresh Start Lesson → open step 0 (do NOT send forceStart/
                              // replay unless the learner explicitly chose Start over).
                              let opts = {};
                              if (isActuallyMastered) {
                                opts = {};
                              } else if (canResume) {
                                opts = { segmentIndex: leftOffIdx != null ? leftOffIdx : 0 };
                              } else {
                                opts = { segmentIndex: 0 };
                              }
                              startModule(
                                track.track_id,
                                mod.module_id,
                                scenarioId,
                                isActuallyMastered, // replay flag → ?review=true when mastered
                                tid,
                                opts,
                              );
                            }}
                          >
                            {startLabel}
                          </button>
                          {canResume && (
                            <button
                              type="button"
                              className="btn-small btn-replay"
                              style={{ marginLeft: 6 }}
                              title="Clear saved progress and open step 1"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                startModule(
                                  track.track_id,
                                  mod.module_id,
                                  scenarioId,
                                  false,
                                  resolveModuleTutorialId(mod, primaryTutorialId),
                                  { forceStart: true },
                                );
                              }}
                            >
                              Start over
                            </button>
                          )}
                        </>
                      )}

                      {!canStart && mod.prerequisites?.length > 0 && (
                        <span className="module-prereq-hint" title={mod.prerequisites.join(', ')}>
                          {t('complete_prerequisites', { defaultValue: 'Complete prerequisites first' })}
                        </span>
                      )}
                    </div>

                    {/* Expandable lessons + in-lesson steps (incl. Puzzle) */}
                    {hasLessons && isExpanded && (
                      <div className="module-lessons-list">
                        {/* Pre-test banner — only show for unlocked, non-completed modules */}
                        {canStart && !isActuallyMastered && !dismissedPretests[mod.module_id] && (() => {
                          let quizSegmentId = null;
                          let quizTutorialId = null;
                          try {
                            for (const tut of tutorials) {
                              if (tut.segment_types?.includes('question')) {
                                quizTutorialId = tut.tutorial_id;
                                break;
                              }
                            }
                          } catch { /* safe */ }
                          return (
                            <PreTestBanner
                              domain={domain}
                              moduleId={mod.module_id}
                              moduleTitle={mod.display_name}
                              quizSegmentId={quizSegmentId}
                              tutorialId={quizTutorialId}
                              trackId={track.track_id}
                              onSkipped={() => {
                                try {
                                  curriculumAPI.getTrackProgress(domain, track.track_id).then(({ data: p }) => {
                                    setProgress((prev) => ({ ...prev, [track.track_id]: p }));
                                  });
                                } catch { /* safe */ }
                              }}
                              onDismissed={() =>
                                setDismissedPretests((prev) => ({ ...prev, [mod.module_id]: true }))
                              }
                            />
                          );
                        })()}
                        {tutorials.map((tut, idx) => {
                          const stepList = Array.isArray(tut.segments) ? tut.segments : [];
                          const hasStepList = stepList.length > 0;
                          return (
                          <div key={tut.tutorial_id} className="lesson-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                              {isMultiLesson && <span className="lesson-index">{idx + 1}</span>}
                              <div className="lesson-info" style={{ flex: 1 }}>
                                <span className="lesson-title">
                                  {isMultiLesson
                                    ? (tut.title || tut.tutorial_id)
                                    : (hasStepList ? `${stepList.length} lesson steps` : 'Lesson steps')}
                                </span>
                                {/* Only show type chips here when we lack per-step titles
                                    (older API). Otherwise they duplicate the header badges. */}
                                {!hasStepList && tut.segment_types?.length > 0 && (
                                  <div className="lesson-segments">
                                    {uniqueTypeBadges(tut.segment_types).map((label) => (
                                      <span key={label} className="segment-badge">
                                        {label}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {canStart && !hasStepList && (
                                <button
                                  type="button"
                                  className="btn-small btn-start"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    startModule(track.track_id, mod.module_id, null, false, tut.tutorial_id);
                                  }}
                                >
                                  Open
                                </button>
                              )}
                            </div>
                            {hasStepList && (
                              <ol className="module-step-list" style={{
                                margin: '8px 0 0',
                                padding: '0 0 0 8px',
                                listStyle: 'none',
                                width: '100%',
                              }}>
                                {stepList.map((seg, stepIdx) => (
                                  <li key={`${tut.tutorial_id}-${seg.index ?? stepIdx}`} style={{
                                    marginBottom: 6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    flexWrap: 'wrap',
                                    padding: '6px 8px',
                                    borderRadius: 8,
                                    background: 'rgba(255,255,255,0.55)',
                                  }}>
                                    <span style={{
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      color: 'var(--brand-primary, #8B1A1A)',
                                      minWidth: 22,
                                    }}>
                                      {stepIdx + 1}.
                                    </span>
                                    <span className="segment-badge">
                                      {labelForSegmentType(seg.segment_type, tut, seg)}
                                    </span>
                                    <span style={{ fontSize: '0.9rem', flex: 1, minWidth: 140, color: '#1f2937' }}>
                                      {seg.title || SEGMENT_LABELS[seg.segment_type] || seg.segment_type}
                                    </span>
                                    {canStart && (
                                      <button
                                        type="button"
                                        className="btn-small btn-start"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          startModule(
                                            track.track_id,
                                            mod.module_id,
                                            null,
                                            false,
                                            tut.tutorial_id,
                                            { segmentIndex: seg.index },
                                          );
                                        }}
                                      >
                                        Go
                                      </button>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
                }; // end renderModule

                return (
                  <>
                    {primaryMods.map((mod) => renderModule(mod))}

                    {/* Supplemental / archetype-filtered modules */}
                    {supplementalMods.length > 0 && (
                      <div className="supplemental-section">
                        <button
                          className="supplemental-toggle"
                          onClick={() => toggleSupplemental(track.track_id)}
                          aria-expanded={!isSupplementalCollapsed}
                        >
                          <span>Additional Resources ({supplementalMods.length})</span>
                          <span className="supplemental-chevron">
                            {isSupplementalCollapsed ? '▼' : '▲'}
                          </span>
                        </button>
                        {!isSupplementalCollapsed && (
                          <div className="supplemental-modules">
                            {supplementalMods.map((mod) => (
                              <div key={mod.module_id} style={{ position: 'relative' }}>
                                <span className="optional-pill">Optional</span>
                                {renderModule(mod)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          );
        })}
      </div>

      </div>
    </div>
  );
}
